<template>
  <div>
    <page-header
      :actions="pageHeaderActionsConfig"
      left-side-class="col-xl-5"
      :page="{
        icon: 'fas fa-user-circle',
        title: 'Manejo de Reembolso y Pago Directo',
        routes: ['Inicio', 'Estudiante', 'Manejo de Reembolso y Pago Directo'],
      }"
      right-side-class="col-xl-7 align-items-end d-flex pb-3 justify-content-end"
    />

    <card-status-transition
      v-if="toggleStatusCard"
      :role="currentUserRole"
      :statuses="availableStatuses"
      title="Cambiar estatus de la factura"
      @on-close="toggleStatusCard = false"
      @on-continue="onSwitchStatus"
    />

    <card-status-readonly
      v-if="showStatusCardReadonly"
      :comment="this.infoData.statusComment"
      :title="this.infoData.statusName"
    />

    <create-adjustment
      v-if="toggleAdjustmentCard"
      @on-close="toggleAdjustmentCard = false"
      @on-save="onSaveAdjustmentHandler($event)"
    />

    <board class="mb-4" :details="detailsBoard" />

    <custom-tab
      key="f"
      class="custom-nav-container custom-nav-colors font-18"
      content-class="mt-3 pb-5 font-18"
      :nav-class="'font-18'"
      :no-nav-style="true"
    >
      <custom-tab-item
        :active="currentIndexTab == detailsInfoTabs.Resumen"
        class="mt-3"
        lazy
        title="Resumen"
        @click="onActiveTab(detailsInfoTabs.Resumen)"
      >
        <reinbursement-resume
          v-if="infoData"
          id="reinbursement-resume-provider"
          :key="resumeKey"
          ref="reinbursementResumeRef"
          :data="infoData"
          :is-read-only="isReadOnly"
          :proposal-documents="infoData.proposalDocuments"
          @on-amount-change="updateAmount"
          @on-check-period="updatePeriods"
          @on-comment-change="updateComment"
          @refresh-data="onRefreshData"
        />
      </custom-tab-item>
      <custom-tab-item
        v-if="!isNewStatus"
        :active="currentIndexTab == detailsInfoTabs.Historial"
        class="mt-3"
        lazy
        title="Historial"
        @click="onActiveTab(detailsInfoTabs.Historial)"
      >
        <tracking-history :request-id="requestId" />
      </custom-tab-item>
      <custom-tab-item
        v-if="canShowAdjustments"
        :active="currentIndexTab == detailsInfoTabs.Ajustes"
        class="mt-3"
        lazy
        title="Ajustes"
        @click="onActiveTab(detailsInfoTabs.Ajustes)"
      >
        <adjustment-history
          ref="adjustmentHistoryRef"
          :data="infoData"
          @on-delete-adjustment="onDeleteAdjustmentHandler($event)"
          @on-edit-adjustment="onEditAdjustmentHandler($event)"
        />
      </custom-tab-item>
      <custom-tab-item
        v-if="canShowPayments"
        :active="currentIndexTab == detailsInfoTabs.Pagos"
        class="mt-3"
        lazy
        title="Pagos"
        @click="() => showPaymentTab()"
      >
        <payment-view
          ref="paymentViewRef"
          v-model="payment"
          :academic-year-id="infoData.academicYearId"
          :can-manually-set-voucher-id="canManuallySetVoucherId"
          :determination-id="infoData.determinationId"
          :disbursement-amount="infoData.amountToRefund"
          :disbursement-info.sync="paymentInfo"
          :disbursement-status="infoData.statusId"
          :documents="infoData.documents"
          :payment-model="infoData.paymentId"
          :request-id="infoData.requestId"
          :tap-request-id="infoData.tapRequestId"
          :year="infoData.year"
          @delete-digit="deleteBillingDigit($event)"
          @on-update-po="onUpdateVoucherStatusHandler"
          @saveDigit="saveDisbursementDigits()"
        />
      </custom-tab-item>
    </custom-tab>
  </div>
</template>

<script>
import moment from 'moment';
import PageHeader from 'PageHeader';
import Board from 'Board';
import { CustomTab, CustomTabItem } from 'custom-tabs';
import {
  getRequestDetails as _getRequestDetails,
  getDetailsByTapRequest as _getDetailsByTapRequest,
  getAvailableStatuses as _getAvailableStatuses,
  switchStatus as _switchStatus,
  saveAdjustment as _saveAdjustment,
  deleteAdjustment as _deleteAdjustment,
  editAdjustment as _editAdjustment,
} from '@/modules/reinbursement/api.js';
import disbursementRequestStatuses from '@/utils/shared/constants/disbursementRequestStatuses';
import ReinbursementResume from './components/details/ReinbursementResume.vue';
import CreateAdjustment from './components/CreateAdjustment.vue';
import CardStatusTransition from '@/components/common/CardStatusTransition.vue';
import CardStatusReadonly from '@/components/common/CardStatusReadonly.vue';
import { mapState } from 'vuex';
import alertMixin from '@/mixins/alert.mixin';
import storeMixin from '@/shared/mixins/storeGenerator';
import TrackingHistory from './components/details/TrackingHistory.vue';
import AdjustmentHistory from './components/details/AdjustmentHistory.vue';
import PaymentView from './components/details/PaymentView.vue';
import {
  _getPaymentTabInfo,
  _saveBillingPurchaseOrderNumber,
  _deleteBillingPurchaseOrderNumber,
} from '@/modules/billingHistory/api';
import { updateVoucherBill } from '@/modules/sifdeVendor/api.js';
import { getSysparameterByNames as _getSysparameterByNames } from '@/modules/shared/sysparameter.api';
import sysparameterByNames from '@/utils/constants/sysParameterNames';
import AppServices from '@/modules/app/api.js';
import 'moment/locale/es-do';
import determinationTypes from '@/utils/constants/determinationTypes';

const tabsId = Object.freeze({
  Resumen: 0,
  Historial: 1,
  Ajustes: 2,
  Pagos: 3,
});

export default {
  name: 'ReinbusementDetail',
  components: {
    PageHeader,
    Board,
    CustomTab,
    CustomTabItem,
    ReinbursementResume,
    CardStatusTransition,
    TrackingHistory,
    CreateAdjustment,
    AdjustmentHistory,
    PaymentView,
    CardStatusReadonly,
  },
  mixins: [alertMixin, storeMixin],
  data() {
    return {
      infoData: null,
      currentIndexTab: 0,
      toggleStatusCard: false,
      toggleAdjustmentCard: false,
      availableStatuses: [],
      periods: [],
      amount: null,
      comment: null,
      academicYearForRefundCertificationParameter: 0,
      /** Payment */
      payment: {
        paymentId: null,
        purchaseOrderNumber: null,
        purchaseOrderNumberId: null,
        amount: 0,
      },
      paymentInfo: {
        isPartialPayment: false,
        addDigitForm: false,
        isTryChangeStatus: false,
        //Collections
        disbursementPurchaseOrderNumbers: [],
        purchaseOrderNumbers: [],
        //For Partial
        payments: [],
        //Calculated
        totalPurchase: 0,
        //Voucher Number & PaymentRefer
        voucherNumber: '',
        voucherDate: null,
        paymentDate: null,
        checkNumber: '',
        requireVoucherNumber: false,
        voucherStatus: null,
        purchaseOrderId: null,
        purchaseOrderPk: null,
        voucherTrackingInfo: {},
        selectedPurchaseOrderBudgetYear: null,
        isBulk: false,
      },
      /** End Payment */
      canManuallySetVoucherId: false,
      resumeKey: 0,
    };
  },
  computed: {
    isRefund() {
      return determinationTypes.Refund === this.infoData?.determinationId;
    },
    isDirectPayment() {
      return (
        determinationTypes.DirectPayment === this.infoData?.determinationId
      );
    },
    isBuyingService() {
      return (
        determinationTypes.BuyingService === this.infoData?.determinationId
      );
    },
    typeName() {
      return determinationTypes.Refund === this.infoData?.determinationId
        ? 'reembolso'
        : 'pago directo';
    },
    ...mapState('$_user', ['user']),
    requestId() {
      return this.$route.params.requestId != null
        ? +this.$route.params.requestId
        : 0;
    },
    tapRequestId() {
      return this.$route.params.tapRequestId != null
        ? +this.$route.params.tapRequestId
        : 0;
    },
    institutionId() {
      return this.$route.params.institutionId != null
        ? +this.$route.params.institutionId
        : 0;
    },
    statusCardTitle() {
      return this.isDirectPayment &&
        this.infoData?.statusId !=
          disbursementRequestStatuses.DevueltoARevisión &&
        this.infoData?.statusComment
        ? 'Razón de estatus'
        : 'Cambiar estatus de la factura';
    },
    showStatusCardReadonly() {
      return (
        this.isDirectPayment &&
        this.infoData?.statusId ==
          disbursementRequestStatuses.DevueltoARevisión &&
        this.infoData?.statusComment
      );
    },
    detailsInfoTabs() {
      return tabsId;
    },
    pageHeaderActionsConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => this.onBack(),
        },
        {
          visible: this.canShowSubmitAction,
          text: this.isDirectPayment
            ? 'Someter Pago Directo'
            : 'Someter Reembolso',
          icon: 'fas fa-flag-alt',
          variant: 'success ml-3',
          click: () => this.submitRequest(),
        },
        {
          text: 'Cambiar Estatus',
          icon: 'fas fa-exchange-alt',
          variant: 'success',
          visible: this.canChangeStatus,
          click: () => (this.toggleStatusCard = true),
        },
        {
          text: 'Certificación de Reembolso',
          icon: 'fal fa-download',
          variant: 'success',
          visible: this.isRefund && this.canDownloadCertificationRefundReport,
          click: () => this.downloadCertificationRefundReport(),
        },
        {
          text: 'Certificación de Pago Directo',
          icon: 'fal fa-download',
          variant: 'success',
          visible:
            this.isDirectPayment &&
            this.canDownloadCertificationDirectPaymentReport,
          click: () => this.downloadCertificationDirectPaymentReport(),
        },
        {
          text: 'Ajustar Solicitud',
          icon: 'fas fa-money-check-alt',
          variant: 'success',
          visible: this.canAdjust,
          click: () => (this.toggleAdjustmentCard = true),
        },
      ];
    },
    isNewStatus() {
      return this.infoData?.statusId === disbursementRequestStatuses.Nuevo;
    },
    isApprovedStatus() {
      return this.infoData.statusId === disbursementRequestStatuses.Aprobado;
    },
    requiredDocumentInPaymentProcess() {
      return (
        this.infoData.statusId ===
          disbursementRequestStatuses.EnProcesoDePago &&
        !this.paymentInfo?.voucherNumber
      );
    },
    canChangeStatus() {
      return (
        [
          disbursementRequestStatuses.Sometido,
          disbursementRequestStatuses.SometidoRevisado,
          disbursementRequestStatuses.PendienteDeAprobación,
          disbursementRequestStatuses.Aprobado,
          disbursementRequestStatuses.ProcesoDeRevisión,
          disbursementRequestStatuses.EnviadoAFinanzas,
          disbursementRequestStatuses.EnProcesoDePago,
          disbursementRequestStatuses.DevueltoARevisión,
        ].includes(this.infoData?.statusId) &&
        !this.toggleStatusCard &&
        ((this.isRefund &&
          this.userHasPermissions(
            'provider.disbursement.status.transition.change'
          )) ||
          (this.isDirectPayment &&
            this.userHasPermissions(
              'provider.disbursement.changestatusdirectpayment'
            )) ||
          this.isBuyingService)
      );
    },
    canDownloadCertificationRefundReport() {
      return (
        [
          disbursementRequestStatuses.PendienteDeAprobación,
          disbursementRequestStatuses.Aprobado,
          disbursementRequestStatuses.EnviadoAFinanzas,
          disbursementRequestStatuses.EnProcesoDePago,
          disbursementRequestStatuses.Pagado,
        ].includes(this.infoData?.statusId) &&
        this.infoData.academicYearId >=
          this.academicYearForRefundCertificationParameter &&
        this.academicYearForRefundCertificationParameter > 0
      );
    },
    canDownloadCertificationDirectPaymentReport() {
      return [
        disbursementRequestStatuses.PendienteDeAprobación,
        disbursementRequestStatuses.Aprobado,
        disbursementRequestStatuses.EnviadoAFinanzas,
        disbursementRequestStatuses.EnProcesoDePago,
        disbursementRequestStatuses.Pagado,
      ].includes(this.infoData?.statusId);
    },
    canAdjust() {
      return (
        this.infoData?.statusId ==
          disbursementRequestStatuses.ProcesoDeRevisión &&
        !this.toggleAdjustmentCard &&
        this.currentIndexTab == tabsId.Ajustes &&
        this.userHasPermissions('provider.disbursement.adjustment')
      );
    },
    canShowSubmitAction() {
      return (
        this.infoData?.statusId == disbursementRequestStatuses.Nuevo ||
        (this.isDirectPayment &&
          this.infoData?.statusId ==
            disbursementRequestStatuses.DevueltoARevisión &&
          this.canSubmitDisbursement)
      );
    },
    canSubmitDisbursement() {
      return (
        this.userHasPermissions(
          'provider.disbursement.submitdirectpaymentinreturnedreview'
        ) || this.user.profile.sub == this.infoData?.ownerId
      );
    },
    isReadOnly() {
      return (
        (this.isRefund &&
          this.infoData?.statusId != disbursementRequestStatuses.Nuevo) ||
        (this.isDirectPayment &&
          this.infoData?.statusId !=
            disbursementRequestStatuses.DevueltoARevisión &&
          this.infoData?.statusId != disbursementRequestStatuses.Nuevo) ||
        (this.isBuyingService &&
          this.infoData?.statusId !=
            disbursementRequestStatuses.DevueltoARevisión &&
          this.infoData?.statusId != disbursementRequestStatuses.Nuevo)
      );
    },
    canShowAdjustments() {
      const statusId = this.infoData?.statusId;
      const determinationType = this.infoData?.determinationId;
      if (
        statusId === disbursementRequestStatuses.SometidoRevisado &&
        determinationType === determinationTypes.DirectPayment
      ) {
        return false;
      }

      return [
        disbursementRequestStatuses.ProcesoDeRevisión,
        disbursementRequestStatuses.SometidoRevisado,
        disbursementRequestStatuses.PendienteDeAprobación,
        disbursementRequestStatuses.Aprobado,
        disbursementRequestStatuses.EnviadoAFinanzas,
        disbursementRequestStatuses.Pagado,
        disbursementRequestStatuses.Cancelado,
        disbursementRequestStatuses.EnProcesoDePago,
      ].includes(statusId);
    },
    canShowPayments() {
      return [
        disbursementRequestStatuses.Aprobado,
        disbursementRequestStatuses.EnviadoAFinanzas,
        disbursementRequestStatuses.EnProcesoDePago,
        disbursementRequestStatuses.Pagado,
      ].includes(this.infoData?.statusId);
    },
    detailsBoard() {
      let items = [
        {
          label: 'Año Académico:',
          icon: 'fas fa-calendar-edit',
          data: this.infoData?.academicYearDescription,
          cols: 'col-md-3 col-12',
        },
        {
          label: 'Número de Solicitud:',
          icon: 'fa fa-file-invoice',
          data: this.infoData?.requestNumber ?? '-',
          cols: 'col-md-3 col-12',
        },
        {
          label: 'Nombre del Estudiante:',
          icon: 'fas fa-address-book',
          data: this.infoData?.studentNameSie,
          cols: 'col-md-3 col-12',
        },
        {
          label: 'Estatus:',
          icon: 'fas fa-ballot-check',
          data: this.infoData?.statusName,
          cols: 'col-md-3 col-12',
        },
        {
          label: 'Número de Consulta:',
          icon: 'fa fa-file-invoice',
          data: this.infoData?.tapRequestNumber,
          cols: 'col-md-3 col-12 mt-3',
        },
        {
          label: 'Determinación:',
          icon: 'fas fa-ballot-check',
          data: this.infoData?.determination,
          cols: 'col-md-3 col-12 mt-3',
        },
        {
          label: 'Periodo:',
          icon: 'fas fa-calendar',
          data: this.selectedPeriods(this.infoData?.periods),
          cols: 'col-md-3 col-12 mt-3',
        },
      ];
      // Insert right before period column
      items.splice(6, 0, {
        label: 'Vigencia:',
        icon: 'fas fa-calendar-edit',
        cols: 'col-md-3 mt-3',
        data: this.infoData?.assignmentDateToShow,
      });

      return [items];
    },
    currentUserRole() {
      return this.userRole;
    },
    selectedPeriods() {
      return (periods) => {
        if (!periods?.length) return '-';
        const periodNames = periods.map((p) => {
          const monthName = moment()
            .month(p.monthNumber - 1)
            .format('MMMM');

          const capitalizedMonth =
            monthName[0].toUpperCase() + monthName.slice(1).toLowerCase();

          return `${capitalizedMonth} ${p.year}`;
        });

        return periodNames.join(', ');
      };
    },
  },
  async mounted() {
    await this.loadDetails();
    await this.getAcademicYearParameterForRefundCertification();
    this.$watch(
      () => this.$route.params,
      () => {
        this.loadDetails();
      }
    );
  },
  methods: {
    onActiveTab(indexTab) {
      this.currentIndexTab = Number(indexTab);
    },
    updatePeriods(value) {
      this.periods = value;
    },
    updateAmount(value) {
      this.amount = value;
    },
    updateComment(value) {
      this.comment = value;
    },
    async onBack() {
      this.$router.push({
        name: 'Reimbursement',
      });
    },
    async showPaymentTab() {
      this.onActiveTab(this.detailsInfoTabs.Pagos);
      this.paymentInfo.isPartialPayment = false;
      return this.getPaymentInformation();
    },
    async loadDetails() {
      const { data } = this.tapRequestId
        ? await _getDetailsByTapRequest(this.tapRequestId)
        : await _getRequestDetails(this.requestId);
      this.infoData = Object.assign({}, data);
      const { data: statuses } = await _getAvailableStatuses(
        this.requestId ?? 0
      );
      this.availableStatuses = [...statuses];

      this.amount = this.infoData.amount;
      this.comment = this.infoData.comment;

      await this.getDisbursementSendToProviderParameter();

      this.paymentInfo.requireVoucherNumber =
        this.isApprovedStatus || this.requiredDocumentInPaymentProcess;

      this.resumeKey++;
    },
    async onSwitchStatus(model) {
      const { statusName, statusId } = this.infoData;
      const { name: newStatus, id: newStatusId } = model.status;
      const disbursementStatusToValidateDocument = [
        disbursementRequestStatuses.Aprobado,
        disbursementRequestStatuses.EnProcesoDePago,
      ];

      if (statusId === disbursementRequestStatuses.EnviadoAFinanzas) {
        const paymentIdIsValid = await this.$refs.paymentViewRef?.validatePaymentId();
        if (!paymentIdIsValid) {
          this.ShowToast(
            'Alerta',
            'Favor completar la información solicitada.',
            'error'
          );
          return false;
        }
      }

      if (
        disbursementStatusToValidateDocument.includes(statusId) &&
        this.$refs.paymentViewRef
      ) {
        const areDocumentsValid = await this.$refs.paymentViewRef.validateDocument(
          newStatusId
        );

        if (!areDocumentsValid) {
          this.ShowToast(
            'Alerta',
            'Favor completar la información solicitada.',
            'error'
          );
          return false;
        }
      }

      const confirmation = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro(a) que desea cambiar de estatus la solicitud de ${this.typeName} de ${statusName} a ${newStatus}?`,
        'Si',
        'No'
      );

      if (!confirmation) return;

      this.toggleStatusCard = false;

      let validVoucherNumber = null;
      if (
        newStatusId !== disbursementRequestStatuses.EnProcesoDePago &&
        statusId !== disbursementRequestStatuses.Aprobado &&
        statusId !== disbursementRequestStatuses.EnProcesoDePago
      ) {
        if (
          this.canManuallySetVoucherId &&
          this.paymentInfo.requireVoucherNumber
        ) {
          const isVoucherValid = await this.$refs?.paymentViewRef?.validateVoucherNumber();

          if (!isVoucherValid) {
            return await this.showPaymentTab()
              .then(() => this.$refs?.paymentViewRef?.validateVoucherNumber())
              .then(() =>
                this.ShowToast('Alerta', 'El voucher es requerido.', 'error')
              );
          } else {
            validVoucherNumber = await this.$refs?.paymentViewRef?.getVoucherNumber();
          }
        }
      } else {
        validVoucherNumber = await this.$refs?.paymentViewRef?.getVoucherNumber();
      }

      let validVoucherDate = null;
      if (
        newStatusId === disbursementRequestStatuses.EnviadoAFinanzas &&
        this.canManuallySetVoucherId
      ) {
        const isVoucherDateValid = await this.$refs?.paymentViewRef?.validateVoucherDate();

        if (!isVoucherDateValid) {
          return await this.showPaymentTab()
            .then(() => this.$refs?.paymentViewRef?.validateVoucherDate())
            .then(() =>
              this.ShowToast(
                'Alerta',
                'La fecha de voucher es requerida.',
                'error'
              )
            );
        }

        validVoucherDate = await this.$refs?.paymentViewRef?.getVoucherDate();
      }

      let validPaymentDate = null;
      if (
        newStatusId === disbursementRequestStatuses.Pagado &&
        this.canManuallySetVoucherId
      ) {
        const isPaymentDateValid = await this.$refs?.paymentViewRef?.validatePaymentDate();

        if (!isPaymentDateValid) {
          return await this.showPaymentTab()
            .then(() => this.$refs?.paymentViewRef?.validatePaymentDate())
            .then(() =>
              this.ShowToast(
                'Alerta',
                'La fecha de pago es requerida.',
                'error'
              )
            );
        }

        validPaymentDate = await this.$refs?.paymentViewRef?.getPaymentDate();
      }

      const payload = {
        requestId: this.requestId,
        statusComment: model.comment,
        newDisbursementRequestStatus: model.status.id,
        voucherNumber: validVoucherNumber?.voucherNumber,
        paymentId: this.$refs?.paymentViewRef?.paymentId,
        voucherDate: validVoucherDate?.voucherDate,
        paymentDate: validPaymentDate?.paymentDate,
      };

      if (model.status.id === disbursementRequestStatuses.EnviadoAFinanzas) {
        if (this.infoData.amountToRefund === this.paymentInfo.totalPurchase) {
          const result = await this.changeStatus(payload, this.infoData.billId);
          if (result) {
            this.onActiveTab(this.detailsInfoTabs.Resumen);
          }
        } else {
          this.ShowToast(
            'Error',
            'El monto total debe ser igual que el monto de la factura.',
            'error'
          );
        }
      } else {
        const result = await this.changeStatus(payload, this.infoData.billId);
        if (result) {
          this.onActiveTab(this.detailsInfoTabs.Resumen);
          this.onRefreshData();
        }
      }
    },
    async changeStatus(payload, billId) {
      return await _switchStatus(payload)
        .then(async ({ data }) => {
          if (data && data.isValid) {
            this.ShowToast(
              '',
              'La solicitud ha cambiado de estatus exitosamente.',
              'success'
            );

            if (
              data.isValid &&
              payload.newDisbursementRequestStatus ===
                disbursementRequestStatuses.EnviadoAFinanzas
            )
              await updateVoucherBill(data.voucherNumber, billId);
            await this.loadDetails();

            return true;
          } else {
            this.ShowToast('Alerta', data.message || 'error', 'error');
            return false;
          }
        })
        .catch((err) => {
          this.ShowToast(
            'Alerta',
            err.response.data.message || 'error',
            'error'
          );
          return false;
        });
    },
    async submitRequest() {
      await this.$refs.reinbursementResumeRef.submit();
    },
    async downloadCertificationRefundReport() {
      await AppServices.getRefundCertificationReport({
        disbursementRequestId: this.requestId,
      });
    },
    async downloadCertificationDirectPaymentReport() {
      await AppServices.getDirectPaymentCertificationReport({
        id: this.requestId,
      });
    },
    async onRefreshData() {
      await this.loadDetails();
    },
    async onSaveAdjustmentHandler(payload) {
      payload.requestId = this.requestId;

      await _saveAdjustment(payload).then(async ({ data }) => {
        if (data) {
          this.ShowToast(
            '',
            'El ajuste ha sido añadido exitosamente.',
            'success'
          );

          this.toggleAdjustmentCard = false;

          await this.loadDetails();
          await this.$refs.adjustmentHistoryRef.getHistory(1);
        }
      });
    },
    async onDeleteAdjustmentHandler(requestAdjustmentId) {
      const answer = await this.ShowCancelModal(
        'Confirmación',
        '¿Está seguro(a) que desea eliminar el ajuste seleccionado?',
        'Si',
        'No'
      );
      if (answer) {
        await _deleteAdjustment(requestAdjustmentId);
        this.ShowToast(
          'Proceso Exitoso.',
          'El ajuste ha sido eliminado exitosamente.',
          'success'
        );

        await this.loadDetails();
        await this.$refs.adjustmentHistoryRef.getHistory(1);
      }
    },
    async onEditAdjustmentHandler(payload) {
      const answer = await this.ShowCancelModal(
        'Atención',
        '¿Está seguro(a) que desea editar el ajuste seleccionado?',
        'Si',
        'No'
      );
      if (answer) {
        await _editAdjustment(payload);
        this.ShowToast(
          'Proceso Exitoso.',
          'El ajuste ha sido actualizado exitosamente.',
          'success'
        );

        await this.loadDetails();
        await this.$refs.adjustmentHistoryRef.getHistory(1);
      }
    },
    async saveDisbursementDigits() {
      let saveModel = {
        billId: this.infoData.billId,
        PurchaseOrderNumberId:
          typeof this.payment.purchaseOrderNumber?.id === 'string'
            ? Number(this.payment.purchaseOrderNumber?.id.split('-')[0])
            : this.payment.purchaseOrderNumber?.id,
        amount: this.payment.amount,
        isPartial: this.paymentInfo.isPartialPayment,
        paymentId: this.paymentInfo.isPartialPayment
          ? this.payment.paymentId
          : null,
        purchaseOrderId: this.infoData.purchaseOrderId.toString(),
        purchaseOrderBudgetYear: parseInt(this.infoData.academicYearId),
        lineNumber: this.payment.lineNumber,
      };
      try {
        const { data } = await _saveBillingPurchaseOrderNumber(saveModel);

        if (!data.isValid) {
          this.ShowToast('Error', data.message, 'error');
          return;
        }
        this.ShowToast(
          '',
          'El número de cifra ha sido agregado exitosamente.',
          'success'
        );

        const voucherDate = this.paymentInfo?.voucherDate;

        await this.getPaymentInformation();
        this.clearPayments();

        this.paymentInfo.voucherDate = voucherDate;
      } catch {
        this.ShowToast('', 'Error al obtener los datos.', 'error');
      }
    },
    async deleteBillingDigit(item) {
      try {
        const voucherDate = this.paymentInfo.voucherDate;

        await _deleteBillingPurchaseOrderNumber(
          item.billingPaymentId,
          item.purchaseOrderNumberId,
          item.lineNumber
        );
        this.ShowToast('', 'Número de cifra eliminado exitosamente', 'success');
        await this.getPaymentInformation();
        this.clearPayments();

        this.paymentInfo.voucherDate = voucherDate;
      } catch {
        this.ShowToast(
          '',
          'No ha sido posible eliminar el número de cifra seleccionado.',
          'error'
        );
      }
    },
    async getPaymentInformation() {
      this.paymentInfo.payments = []; //Reset
      this.paymentInfo.disbursementPurchaseOrderNumbers = []; //Reset
      if (this.payment == null) {
        this.payment = {
          paymentId: null,
          purchaseOrderNumber: null,
          purchaseOrderNumberId: null,
          amount: 0,
        };
      }
      this.clearPayments();

      try {
        const res = await _getPaymentTabInfo(this.infoData.billId);

        this.paymentInfo.selectedPurchaseOrderId = null;
        //If exist partialPayment is true
        if (res.data.some((s) => s.isPartial)) {
          this.paymentInfo.payments = res.data;
          this.paymentInfo.isPartialPayment = true;
          //Set Last Voucher Numer
          this.paymentInfo.voucherNumber =
            res.data[res.data.length - 1].voucherNumber;
          this.paymentInfo.checkNumber =
            res.data[res.data.length - 1].paymentIdReference;
          this.paymentInfo.voucherDate =
            res.data[res.data.length - 1].voucherDate;
          this.paymentInfo.paymentDate =
            res.data[res.data.length - 1].paymentDate;
        } else if (res.data[0] != undefined) {
          const paymentInfo = res.data[0];
          //Set Bill Information
          if (res.data.length > 1) {
            this.paymentInfo.payments = res.data;
          }

          this.paymentInfo.isPartialPayment = false;
          this.paymentInfo.purchaseOrderPk = paymentInfo.purchaseOrderPk;
          this.paymentInfo.disbursementPurchaseOrderNumbers =
            paymentInfo.billingPurchaseOrderNumbers;
          this.paymentInfo.voucherNumber = paymentInfo.voucherNumber;
          this.paymentInfo.voucherStatus = paymentInfo.voucherStatus;
          this.paymentInfo.checkNumber = paymentInfo.paymentIdReference;
          this.paymentInfo.selectedPurchaseOrderId =
            paymentInfo.purchaseOrderId;
          this.paymentInfo.selectedPurchaseOrderBudgetYear =
            paymentInfo.purchaseOrderBudgetYear;
        } else if (this.paymentInfo.isPartialPayment == null) {
          this.paymentInfo.isPartialPayment = false; //For default
        } else if (
          this.paymentInfo.payments.every((p) => p.purchaseTotal != 0)
        ) {
          this.paymentInfo.payments = [];
        }
        this.paymentInfo.isBulk = res?.data[0]?.isBulk ?? false;
        this.paymentInfo.voucherDate = res?.data[0]?.voucherDate ?? null;
        this.paymentInfo.paymentDate = res?.data[0]?.paymentDate ?? null;
      } catch (e) {
        this.ShowToast(
          '',
          'Error al obtener los datos, detalles: ' + e.message,
          'error'
        );
      }
    },
    clearPayments() {
      this.payment.amount = 0;
      this.payment.paymentId = null;
      this.payment.purchaseOrderNumber = null;
      this.payment.purchaseOrderNumberId = null;

      this.paymentInfo.addDigitForm = false;
    },
    async onUpdateVoucherStatusHandler(voucherNumber) {
      if (voucherNumber) {
        await updateVoucherBill(voucherNumber, this.infoData.billId);
        await this.getPaymentInformation();
        this.ShowToast(
          '',
          'El PO ha sido actualizado exitosamente.',
          'success'
        );
      }
    },
    async getDisbursementSendToProviderParameter() {
      const { data } = await _getSysparameterByNames({
        parameterNames: [
          sysparameterByNames.DisbursementSendToProvider,
          sysparameterByNames.DirectPaymentSifde,
        ],
      });

      this.canManuallySetVoucherId = this.isRefund
        ? data
            .find(
              (x) => x.name == sysparameterByNames.DisbursementSendToProvider
            )
            .staticValue?.toLowerCase() === 'true'
        : this.isDirectPayment
        ? data
            .find((x) => x.name == sysparameterByNames.DirectPaymentSifde)
            .staticValue?.toLowerCase() === 'false'
        : null;
    },
    async getAcademicYearParameterForRefundCertification() {
      const { data: sysParameters } = await _getSysparameterByNames({
        parameterNames: [
          sysparameterByNames.AcademicYearDownloadRefundCertificationReport,
        ],
      });
      if (sysParameters && sysParameters.length) {
        this.academicYearForRefundCertificationParameter =
          Number(sysParameters[0].staticValue) ?? 0;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/shared/scss/config/_colors.scss';

.custom-nav-container ::v-deep {
  .custom-tab-container {
    border-bottom: 1px solid map-get($colors, 'gray-5') !important;
    margin-bottom: 20px !important;
    .nav-item {
      .nav-link {
        font-size: 18px !important;
        color: map-get($colors, 'gray-11') !important;
        &.active {
          border-bottom: 3px solid map-get($colors, 'orange') !important;
          color: map-get($colors, 'orange') !important;
          font-weight: bold !important;
        }
        &.active:after {
          content: unset !important;
        }
      }
    }
  }
  .custom-tab-cursors {
    color: #ff7058;
  }
}
</style>
